// Éditeur de niveau (lot 3) : dessin à la souris sur une grille 28x16, palette DOM,
// annuler/refaire, enregistrement via l'API. Le rendu est STATIQUE (redessin sur
// changement, PAS de boucle de jeu). Les tuiles utilisent les mêmes sprites que le jeu
// (client/sprites.ts) pour une continuité visuelle avec les parties.
//
// Le canvas passé (le #game du jeu) est DÉPLACÉ dans l'éditeur le temps de l'édition,
// à l'échelle grille (case de 32 px), puis rendu au jeu (dimensions d'origine) au stop.
//
// Sécurité (revue T3) : tout texte d'origine utilisateur (nom de carte, message serveur)
// est inséré via textContent, JAMAIS innerHTML — les noms n'ont pas de filtre de charset
// (risque de XSS stocké). Les libellés de la palette sont des chaînes statiques.
import type { LevelFull } from '../shared/api-types'
import { levelFromAscii, validateLevel } from '../shared/level'
import { clampDim, normalize } from './editor-grid'
import { WORLDS, WORLD_KEYS, type WorldKey, toWorld } from '../shared/worlds'
import { COLS, MAX_COLS, MAX_MONKS, MAX_ROWS, MONK_SPEEDS, type MonkSpeed, ROWS, T, cellIndex, colOf, rowOf, toMonkSpeed } from '../shared/types'
import { ApiError, apiCreateLevel, apiFinishedLevel, apiPutOfficialLevel, apiUpdateLevel } from './api'
import { newLocalId, saveDraft } from './maps-offline'
import { mkById } from './util'
import { startSolo, type SoloHandle } from './solo'
import { drawBigDeco } from './render'
import {
  BOMB_ITEM, CRATE_BOULDER, CRATE_METAL, CRATE_WOOD, DECO_SPRITES, GOLD, JACK_ITEM, KEY_ITEM, MONK_FRAMES, monkSprite, ROPE_ITEM, RUNNER_FRAMES, TAZER_ITEM, TILE_SPRITES, runner2, runner3, type SpriteRef,
  WORLD_TILES, WORM_SLEEP, WORM_WAKE, FISH_FRAMES } from './sprites'
import { BIG_DECOS, DECOS, type BigDecoKind, type DecoKind, toDecorString } from '../shared/decor'
import { blitVignette } from './vignette-papier'
import { blitObjet } from './objet-papier'
import { type View, dist, midpoint, pinch, pan, clampView } from './gestures'
import { isTouch } from './platform'

// Décorations (couche cosmétique) : caractère d'éditeur -> sprite, l'ensemble des chars déco, et
// le sentinel « gomme déco » (jamais stocké dans la grille). Dérivés de DECOS (source de vérité).
const DECO_BY_CHAR = new Map<string, SpriteRef>()
for (const k of Object.keys(DECOS) as DecoKind[]) DECO_BY_CHAR.set(DECOS[k].ch, DECO_SPRITES[k])
for (const k of Object.keys(BIG_DECOS) as BigDecoKind[]) DECO_BY_CHAR.set(BIG_DECOS[k].ch, null as never) // grands décors : pas de SpriteRef (dessin procédural drawBigDeco)
const DECO_CHARS = new Set(DECO_BY_CHAR.keys())
const BIG_BY_CHAR = new Map<string, BigDecoKind>((Object.keys(BIG_DECOS) as BigDecoKind[]).map((k) => [BIG_DECOS[k].ch, k]))
const DECO_ERASE = '§' // « gomme déco » : efface la couche déco d'une case sans toucher le terrain

// Instantané d'historique : les DEUX couches (terrain + déco) + l'alternance des téléporteurs
// (tpNext, avancée en EFFET DE BORD dès la pose d'une extrémité), pour un annuler/refaire/rollback
// cohérent — sans tpNext, annuler ou interrompre un trait téléporteur poserait ensuite la mauvaise
// extrémité (sortie au lieu d'entrée) et casserait la paire.
interface Snap { rows: string[]; deco: string[]; tp: Record<string, 0 | 1> }

export interface EditorOpts {
  canvas: HTMLCanvasElement
  sheet: HTMLImageElement
  // undefined = nouvelle carte. `id` ABSENT = copie à créer (ex. « modifier une copie » d'une
  // carte officielle du catalogue, 16/07 nuit) : la sauvegarde passera par apiCreateLevel.
  // `localId` (25/07) : on modifie une carte créée SANS RÉSEAU, pas encore publiée. Elle n'a pas
  // encore de numéro serveur ; l'enregistrement met à jour le brouillon local, et c'est le retour
  // du réseau qui la publiera.
  // `officielSlug` (30/07) : on modifie une carte OFFICIELLE du catalogue, en tant qu'admin.
  // L'enregistrement écrit alors une SURCHARGE (server/admin-levels) au lieu de créer une copie —
  // le slug ne change pas, donc les records de la famille restent rattachés à la carte.
  initial?: { id?: number; localId?: string; officielSlug?: string; name: string; rows: string[]; world: WorldKey; monkSpeed: MonkSpeed; smartMonks: boolean; monkPowers: boolean; decor: string }
  onQuit: () => void // retour à l'écran appelant (Mes niveaux, Task 7 ; menu via le crochet dev)
}
export interface EditorHandle { stop(): void }

// Échelle de l'éditeur : 2x (case de 32 px). Grille 896x512 px, redimensionnée en CSS
// pour tenir à côté de la palette (image-rendering: pixelated conserve le style).
const ED_SCALE = 2
const ED_CELL = 16 * ED_SCALE
const MAX_HISTORY = 100
const FONT = 'ui-monospace, Menlo, monospace'

// Palette : un bouton par caractère de la légende, groupés. Les téléporteurs sont
// représentés par leur PAIRE (T/U/V) ; l'extrémité (entrée majuscule / sortie minuscule)
// alterne à chaque pose sur la grille.
interface Tile { ch: string; label: string }
const GROUPS: { title: string; tiles: Tile[] }[] = [
  { title: 'Terrain', tiles: [
    { ch: '#', label: 'Brique' },
    { ch: '=', label: 'Béton' },
    { ch: 'H', label: 'Échelle' },
    { ch: '-', label: 'Corde' },
    { ch: 'x', label: 'Fausse brique' },
    { ch: 'I', label: 'Glace' },
    { ch: 's', label: 'Sable' },
    { ch: 'c', label: 'Sol friable' },
    { ch: 'L', label: 'Lave' },
    { ch: '~', label: 'Eau (nage)' }, // EAU 16/07 : non solide, nage 4 directions + souffle
    { ch: ':', label: 'Bulles (recharge)' }, // source au FOND de l'eau : seule cette case recharge le souffle
    { ch: '_', label: 'Checkpoint (ver 5 cases)' }, // ANCRE = centre du ver ; le toucher = point de réanimation
    { ch: '°', label: 'Interrupteur' }, // occupé (coureur/moine/caisse) = toutes les barrières s'ouvrent (17/07)
    { ch: '¦', label: 'Barrière (circuit)' }, // solide fermée ; s'ouvre quand un interrupteur est pressé
    { ch: '»', label: 'Sens unique droite' }, // traversable gauche->droite seulement (17/07)
    { ch: '«', label: 'Sens unique gauche' },
    { ch: '¤', label: 'Cristal de dash' }, // recharge le cooldown du dash au passage (17/07)
    { ch: '/', label: 'Rampe montante droite' },  // RAMPES 16/07 : hop diagonal
    { ch: '\\', label: 'Rampe montante gauche' },
    { ch: '+', label: 'Feu' },  // FEU 16/07 : mortel au contact, version visible de la zone mortelle
    { ch: '"', label: 'Pics' }, // PICS 16/07 : mortels en tombant dessus seulement
    { ch: 'Z', label: 'Zone mortelle' },
    { ch: '<', label: 'Tapis gauche' },
    { ch: '>', label: 'Tapis droite' },
    { ch: 'n', label: 'Tapis haut' },
    { ch: 'w', label: 'Tapis bas' },
    { ch: '^', label: 'Trampoline' },
    { ch: 'A', label: 'Trampoline puissant' }, // rebond 5 cases (16/07, reste de la demande #7)
    { ch: '{', label: 'Tremplin puissant gauche' }, // directionnel FORT haut-gauche (16/07 soir, C1)
    { ch: '}', label: 'Tremplin puissant droite' }, // directionnel FORT haut-droite
    { ch: '(', label: 'Trampoline ↖' },
    { ch: ')', label: 'Trampoline ↗' },
    { ch: 'R', label: 'Bombes ∞' },
    { ch: 'D', label: 'Porte' },
  ] },
  { title: 'Objets', tiles: [
    { ch: '$', label: 'Or' },
    { ch: 'b', label: 'Bombe' },
    { ch: 'j', label: 'Marteau' },
    { ch: 'z', label: 'Tazer' },
    { ch: 'k', label: 'Clé' },
    { ch: 'r', label: 'Cordes' },
    { ch: 'æ', label: 'Appât' },   // 31/07 : le pilon qui détourne les moines (il manquait à la palette)
    { ch: 'þ', label: 'Potion de géant' }, // 05/08 : bue à la touche action = géant 6 s (écrase les moines, bouscule les autres)
    { ch: 'ø', label: 'Déguisement de moine' }, // 05/08 : enfilé à la touche action = faux moine 10 s (kill au contact en versus)
    { ch: 'œ', label: 'Maillet à ressort' }, // 05/08 : remplace la pioche (échange), frappes infinies, bump 3 cases
    { ch: 'Ł', label: 'Bandelette (momie)' }, // 05/08 : posée à la touche action, elle momifie le premier adversaire qui la traverse
    { ch: 'Ŧ', label: 'Patate chaude' }, // 05/08 : posée ÉTEINTE ; la toucher l'allume (mèche secrète 2-7 s), transfert au contact
    { ch: 'Ŋ', label: 'Meute privée' }, // 05/08 : deux spectres 10 s sur le joueur le plus riche
    { ch: 'ĸ', label: 'Fantôme de papier' }, // 05/08 : 4 s invisible chez les adversaires (les moines, eux, te voient)
    { ch: 'Ħ', label: 'Couronne maudite' }, // 05/08 : +1 pièce/s, mais TOUS les moines te veulent ; ne se lâche qu'à la mort
    { ch: 'E', label: 'Échelle de sortie' },
  ] },
  { title: 'Départs', tiles: [
    { ch: '1', label: 'Joueur 1' },
    { ch: '2', label: 'Joueur 2' },
    { ch: '3', label: 'Joueur 3' },
    { ch: 'm', label: 'Moine' },
    { ch: 'S', label: 'Moine sauteur' },
    { ch: 'G', label: 'Moine voleur' },
    { ch: 'M', label: 'Boss Moine Fou' },
    { ch: 'µ', label: 'Moine bâtisseur' }, // 17/07 : il rebouche les trous creusés (robe ocre ; 2e alphabet, ']' est au rocher)
    { ch: '`', label: 'Statue au gong' },  // 17/07 : dormante ; s'éveille quand un or ADJACENT est ramassé
    { ch: ',', label: 'Piranha (eau)' }, // ennemi aquatique (16/07 soir) : le char pose AUSSI son eau
    { ch: ';', label: 'Méduse (eau, paralyse)' },
    { ch: 'ð', label: 'Drapeau-crapaud' }, // 05/08 : fanion baladeur — l'attraper = forme crapaud (touche creuser pour sortir)
  ] },
  { title: 'Téléporteurs', tiles: [
    { ch: 'T', label: 'Paire A' },
    { ch: 'U', label: 'Paire B' },
    { ch: 'V', label: 'Paire C' },
  ] },
  { title: 'Caisses', tiles: [ // Phase 4 (#2) : bois cassable (lâche son contenu en tombant de ≥2 cases) + métal indestructible
    // Chars renommés 16/07 (X F O N J y Q d) : les initiaux recouvraient des chars de DÉCO et le
    // routage du pinceau est PAR CHAR -> poser une caisse posait une déco (cf. shared/level.ts).
    { ch: 'X', label: 'Caisse métal' },
    { ch: 'F', label: 'Caisse bois (vide)' },
    { ch: 'O', label: 'Caisse bois (or)' },
    { ch: 'N', label: 'Caisse bois (bombe)' },
    { ch: 'J', label: 'Caisse bois (marteau)' },
    { ch: 'y', label: 'Caisse bois (tazer)' },
    { ch: 'Q', label: 'Caisse bois (clé)' },
    { ch: 'd', label: 'Caisse bois (corde)' },
    { ch: ']', label: 'Rocher (roule)' }, // poussé une fois, il roule et écrase tout (17/07)
  ] },
]
// Contenu d'une caisse bois -> sprite affiché en BADGE dans l'éditeur (le jeu, lui, cache le contenu).
const CRATE_CONTENT: Record<string, SpriteRef> = { O: GOLD, N: BOMB_ITEM, J: JACK_ITEM, y: TAZER_ITEM, Q: KEY_ITEM, d: ROPE_ITEM }
/** Le même badge, en papier (29/07) : l'objet ramassable dessiné, pas son sprite. */
const CRATE_BADGE: Record<string, string> = { O: 'or', N: 'bomb', J: 'jackhammer', y: 'tazer', Q: 'key', d: 'rope' }

// Paires de téléporteurs : entrée (majuscule) / sortie (minuscule).
const TP_PAIRS: Record<string, [string, string]> = { T: ['T', 't'], U: ['U', 'u'], V: ['V', 'v'] }
const TP_CHARS = 'TtUuVv'
// Caractères de moine (tous types) : normal 'm', sauteur 'S', voleur 'G', boss 'M'. Servent au
// plafond MAX_MONKS (compté tous types confondus, comme validateLevel côté moteur).
const MONK_CHARS = 'mSGM'
// Libellés FR du menu déroulant « Vitesse des moines » (Task 3). Chaînes STATIQUES (aucune
// origine utilisateur), dans l'ordre de MONK_SPEEDS (lent -> rapide).
const MONK_SPEED_LABELS: Record<MonkSpeed, string> = { lent: 'Moines lents', normal: 'Moines normaux', rapide: 'Moines rapides' }

/** Sprite de base pour un caractère de la légende (null = case vide). */
function baseSprite(ch: string): SpriteRef | null {
  switch (ch) {
    case '#': return TILE_SPRITES[T.Brick]!
    case '=': return TILE_SPRITES[T.Concrete]!
    case 'H': return TILE_SPRITES[T.Ladder]!
    case '-': return TILE_SPRITES[T.Rope]!
    case 'x': return TILE_SPRITES[T.FalseBrick]! // rendu identique à la brique (marqueur ajouté)
    case 'I': return TILE_SPRITES[T.Ice]!
    case 's': return TILE_SPRITES[T.Sand]!
    case 'c': return TILE_SPRITES[T.Crumble]! // sol friable (sprite brique fissurée)
    case 'L': return TILE_SPRITES[T.Lava]!    // lave (sprite magma ondulé)
    case '~': return TILE_SPRITES[T.Water]!   // eau (EAU 16/07 : nage + souffle)
    case ':': return TILE_SPRITES[T.Bubbles]! // source de bulles (recharge le souffle)
    case '_': return WORM_WAKE[4]! // checkpoint : TÊTE du ver éveillé en vignette (le ver complet est dessiné par redraw)
    case '°': return TILE_SPRITES[T.Button]! // interrupteur (17/07)
    case '¦': return TILE_SPRITES[T.Barrier]! // barrière fermée
    case '»': return TILE_SPRITES[T.OneWayR]! // sens unique droite
    case '«': return TILE_SPRITES[T.OneWayL]! // sens unique gauche
    case '¤': return TILE_SPRITES[T.DashCrystal]! // cristal de dash
    case '/': return TILE_SPRITES[T.RampUpRight]!  // rampe diagonale droite (16/07)
    case '\\': return TILE_SPRITES[T.RampUpLeft]! // rampe diagonale gauche (16/07)
    case '+': return TILE_SPRITES[T.Fire]!    // feu (16/07 : mortel au contact)
    case '"': return TILE_SPRITES[T.Spikes]!  // pics (16/07 : mortels en chute)
    case 'Z': return TILE_SPRITES[T.DeathZone]! // #1 : champ mortel traversable (voile électrique)
    case '<': return TILE_SPRITES[T.ConveyorL]!  // tapis roulant vers la gauche
    case '>': return TILE_SPRITES[T.ConveyorR]!  // tapis roulant vers la droite
    case 'n': return TILE_SPRITES[T.ConveyorUp]!  // tapis roulant vertical montant (Lot C)
    case 'w': return TILE_SPRITES[T.ConveyorDown]! // tapis roulant vertical descendant (Lot C)
    case '^': return TILE_SPRITES[T.Trampoline]! // trampoline (rebond)
    case 'A': return TILE_SPRITES[T.TrampolineStrong]! // trampoline puissant (rebond 5 cases, 16/07)
    case '{': return TILE_SPRITES[T.TrampolineStrongL]! // directionnel puissant haut-gauche (C1)
    case '}': return TILE_SPRITES[T.TrampolineStrongR]! // directionnel puissant haut-droite
    case '(': return TILE_SPRITES[T.TrampolineL]! // trampoline directionnel haut-gauche (Lot C)
    case ')': return TILE_SPRITES[T.TrampolineR]! // trampoline directionnel haut-droite (Lot C)
    case 'R': return TILE_SPRITES[T.BombRefill]!  // station de bombes rouges (Lot C)
    case 'D': return TILE_SPRITES[T.DoorClosed]! // porte fermée (solide, Phase 3)
    case '$': return GOLD
    case 'b': return BOMB_ITEM
    case 'j': return JACK_ITEM
    case 'z': return TAZER_ITEM
    case 'k': return KEY_ITEM                    // clé (objet, Phase 3)
    case 'r': return ROPE_ITEM                   // objet corde (#4)
    case 'X': return CRATE_METAL                 // caisse métal (Phase 4, #2 ; chars 16/07)
    case 'F': case 'O': case 'N': case 'J': case 'y': case 'Q': case 'd': return CRATE_WOOD // caisses bois (contenu affiché en badge)
    case ']': return CRATE_BOULDER[0]! // rocher (17/07) : rond, roule quand on le pousse
    case 'E': return TILE_SPRITES[T.ExitLadder]!
    case '1': return RUNNER_FRAMES.idle
    case '2': return runner2(RUNNER_FRAMES.idle)
    case '3': return runner3(RUNNER_FRAMES.idle)
    case 'm': return MONK_FRAMES.run[0]
    case 'S': return monkSprite(MONK_FRAMES.run[0], 'sauteur') // moine sauteur (vert)
    case 'G': return monkSprite(MONK_FRAMES.run[0], 'voleur')  // moine voleur (ambre/or)
    case 'M': return monkSprite(MONK_FRAMES.run[0], 'boss')    // boss Moine Fou (rouge sombre)
    case 'µ': return MONK_FRAMES.run[0] // bâtisseur : frames du normal, l'OCRE vient du filtre de drawCellVisual
    case '`': return MONK_FRAMES.run[0] // statue au gong : idem, GRIS via filtre (pierre)
    case ',': return FISH_FRAMES.piranha[0] // piranha (ennemi aquatique, 16/07 soir)
    case ';': return FISH_FRAMES.meduse[0]  // méduse (paralyse au contact)
    default:
      if (DECO_BY_CHAR.has(ch)) return DECO_BY_CHAR.get(ch)! // décoration (couche cosmétique)
      if (TP_CHARS.includes(ch)) return TILE_SPRITES[T.Teleporter]!
      return null
  }
}

// Tailles de carte proposées dans l'éditeur (Lot E). 28×16 = classique (défaut) ; jusqu'à MAX_COLS×MAX_ROWS.
const SIZE_OPTIONS: { label: string; cols: number; rows: number }[] = [
  { label: `Petite (${COLS}×${ROWS})`, cols: COLS, rows: ROWS },
  { label: 'Moyenne (42×24)', cols: 42, rows: 24 },
  { label: `Grande (${MAX_COLS}×${MAX_ROWS})`, cols: MAX_COLS, rows: MAX_ROWS },
]

export function startEditor(opts: EditorOpts): EditorHandle {
  const { canvas, sheet } = opts
  const officielSlug = opts.initial?.officielSlug ?? null
  const byId = mkById('Éditeur')
  const root = byId('editor')
  const stage = byId('ed-stage')
  const palette = byId('ed-palette')
  const nameInput = byId<HTMLInputElement>('ed-name')
  const worldSelect = byId<HTMLSelectElement>('ed-world')
  const sizeSelect = byId<HTMLSelectElement>('ed-size')
  const monkSpeedSelect = byId<HTMLSelectElement>('ed-monk-speed')
  const smartMonksCheckbox = byId<HTMLInputElement>('ed-smart-monks')
  const monkPowersCheckbox = byId<HTMLInputElement>('ed-monk-powers') // MOINES ARMÉS (16/07)
  const testBtn = byId<HTMLButtonElement>('ed-test')
  const saveBtn = byId<HTMLButtonElement>('ed-save')
  const quitBtn = byId<HTMLButtonElement>('ed-quit')
  const undoBtn = byId<HTMLButtonElement>('ed-undo') // outils de dessin (Phase 3 L1)
  const redoBtn = byId<HTMLButtonElement>('ed-redo')
  const eraseBtn = byId<HTMLButtonElement>('ed-erase')
  const recenterBtn = byId<HTMLButtonElement>('ed-recenter') // zoom/pan (Phase 3 L2) : réinitialise la vue
  const zenBtn = byId<HTMLButtonElement>('ed-zen')           // plein écran de dessin (audit 16/07, C1)
  const zenExitBtn = byId<HTMLButtonElement>('ed-zen-exit')  // ✕ flottant de sortie du plein écran
  const settingsToggleBtn = byId<HTMLButtonElement>('ed-settings-toggle') // ⚙ tiroir réglages (Phase 3 L3, mobile)
  const settingsPanel = byId('ed-settings')
  const statusEl = byId('ed-status')

  // --- état de l'éditeur ---
  // Dimensions de la grille éditée (Lot E) : déduites du niveau chargé (clampées aux bornes),
  // sinon 28×16 (défaut d'une nouvelle carte). Changées via le sélecteur de taille (resizeGrid).
  let edCols = clampDim(opts.initial?.rows?.[0]?.length ?? COLS, MAX_COLS)
  let edRows = clampDim(opts.initial?.rows?.length ?? ROWS, MAX_ROWS)
  let rows: string[] = normalize(opts.initial?.rows, edCols, edRows)
  // Garde anti-perte : instantané de référence de la grille enregistrée. Comparé à la
  // grille courante au moment de quitter pour prévenir si des modifications ne sont pas
  // sauvegardées. Remis à jour après chaque enregistrement réussi (handleSave).
  let savedAscii = rows.join('\n')
  // Couche de décorations (28×16), init du niveau édité (normalisée), sinon vide. Éditée en
  // PARALLÈLE du terrain (2 couches), cosmétique (hors sim). `savedDecor` = réf anti-perte.
  let decoRows: string[] = normalize(toDecorString(opts.initial?.decor).split('\n'), edCols, edRows)
  let savedDecor = decoRows.join('\n')
  let currentId: number | null = opts.initial?.id ?? null
  // Identifiant LOCAL d'une carte pas encore publiée (créée hors ligne). Exclusif de currentId :
  // dès que la carte part au serveur, elle prend un vrai numéro et celui-ci n'a plus lieu d'être.
  let currentLocalId: string | null = opts.initial?.localId ?? null
  // Monde (thème visuel) de la carte : chargé du niveau édité, sinon classique. Piloté par le
  // menu déroulant #ed-world ; alimente le test et l'enregistrement. `savedWorld` = référence
  // anti-perte (comme savedAscii) pour prévenir au Quitter si le monde a changé sans enregistrer.
  let currentWorld: WorldKey = opts.initial?.world ?? 'classique'
  let savedWorld: WorldKey = currentWorld
  // Vitesse des moines (Task 3) de la carte : chargée du niveau édité, sinon 'normal'. Pilotée
  // par le menu #ed-monk-speed ; alimente le test et l'enregistrement. `savedMonkSpeed` = réf
  // anti-perte (comme savedWorld) pour prévenir au Quitter si la vitesse a changé sans enregistrer.
  let currentMonkSpeed: MonkSpeed = opts.initial?.monkSpeed ?? 'normal'
  let savedMonkSpeed: MonkSpeed = currentMonkSpeed
  // IA maligne (smartMonks) de la carte : chargée du niveau édité, sinon false. Pilotée par la case
  // à cocher #ed-smart-monks ; alimente le test et l'enregistrement. `savedSmartMonks` = référence
  // anti-perte (comme savedMonkSpeed) pour prévenir au Quitter si l'option a changé sans enregistrer.
  let currentSmartMonks: boolean = opts.initial?.smartMonks ?? false
  // Moines ARMÉS (16/07) : même mécanique d'option de carte que l'IA maligne (case + saved).
  let currentMonkPowers: boolean = opts.initial?.monkPowers ?? false
  let savedSmartMonks: boolean = currentSmartMonks
  let savedMonkPowers: boolean = currentMonkPowers
  let selected = '#' // tuile active (brique par défaut)
  let activeGroupTitle = 'Terrain' // onglet de palette actif (Phase 3 L3) ; conservé au rebuild (monde)
  const undoStack: Snap[] = []
  const redoStack: Snap[] = []
  const tpNext: Record<string, 0 | 1> = { T: 0, U: 0, V: 0 }
  const snapshot = (): Snap => ({ rows: rows.slice(), deco: decoRows.slice(), tp: { ...tpNext } })
  // « Rien changé ? » se décide sur le terrain + la déco seuls : tpNext n'avance QUE lors d'une pose
  // de téléporteur, qui modifie déjà la grille -> pas besoin de le comparer ici.
  const sameSnap = (a: Snap, b: Snap): boolean => a.rows.join('\n') === b.rows.join('\n') && a.deco.join('\n') === b.deco.join('\n')
  let painting = false
  let eraseMode = false          // gomme du TRAIT courant (bascule Gomme OU clic droit souris)
  let eraseToggle = false        // état persistant du bouton Gomme (Phase 3 L1) : dessine en effaçant
  let activePointerId = -1       // pointeur qui peint (-1 = aucun) ; les autres sont ignorés
  let strokeBefore: Snap | null = null
  let lastCell = -1
  let hoverCell = -1
  let stopped = false
  // Zoom/pan (Phase 3 L2) : transform CSS COSMÉTIQUE du canvas (hors sim/hash). `pointers` suit les
  // doigts posés ; dès 2 doigts on bascule en GESTE (pincement + glissé) et la peinture est suspendue.
  let view: View = { scale: 1, tx: 0, ty: 0 }
  const pointers = new Map<number, { x: number; y: number }>()
  let gesture: {
    ids: [number, number]; startView: View
    startDist: number; startMidX: number; startMidY: number; focusLX: number; focusLY: number
  } | null = null
  let testHandle: SoloHandle | null = null // partie de test en cours (null = édition)

  // --- prise en main du canvas : le canvas #game est PARTAGÉ entre l'éditeur (échelle
  // grille) et la partie de test (échelle jeu, Task 6). On mémorise ses dimensions et son
  // parent d'origine pour basculer dans les deux sens. Redimensionner un canvas réinitialise
  // son contexte : on redésactive donc le lissage à chaque bascule.
  const originalParent = canvas.parentElement
  const originalW = canvas.width
  const originalH = canvas.height
  const ctx = canvas.getContext('2d')!
  function useEditorCanvas(): void {
    stage.appendChild(canvas)
    canvas.width = edCols * ED_CELL   // Lot E : la grille éditée peut dépasser 28×16 (CSS l'ajuste au stage)
    canvas.height = edRows * ED_CELL
    ctx.imageSmoothingEnabled = false
    view = { scale: 1, tx: 0, ty: 0 } // repart à la vue naturelle (L2) : nouveau niveau/retour de test/redim
    applyTransform()
    initialZoom() // grande carte sur tactile : cadre d'emblée la zone construite (audit 16/07, C2)
  }
  function useGameCanvas(): void {
    canvas.width = originalW
    canvas.height = originalH
    ctx.imageSmoothingEnabled = false
    canvas.style.transform = ''; canvas.style.transformOrigin = '' // le jeu rend le canvas SANS transform éditeur (L2)
    if (originalParent) originalParent.appendChild(canvas)
  }
  useEditorCanvas()

  // --- rendu ---------------------------------------------------------------
  function drawCellVisual(g: CanvasRenderingContext2D, ch: string, x: number, y: number, size: number): void {
    // GRAND DÉCOR (17/07) : vignette = l'ensemble entier réduit dans la case (figé, t=0).
    // Dans la GRILLE, ces chars sont dessinés en overlay pleine taille par redraw (skippés ici).
    const bigK = BIG_BY_CHAR.get(ch)
    if (bigK) {
      const b = BIG_DECOS[bigK]
      const sc = size / (Math.max(b.w, b.h) * 16)
      g.save()
      g.translate(x + (size - b.w * 16 * sc) / 2, y + (size - b.h * 16 * sc) / 2)
      g.scale(sc, sc)
      drawBigDeco(g, bigK, 0, 0, 0)
      g.restore()
      return
    }
    // REFONTE « papier découpé » (29/07, demande Claude-Alain) : la case se DESSINE,
    // dans le même langage que le jeu. Un char sans dessin papier retombe sur
    // l'ancien sprite — aucune case de l'éditeur ne peut se retrouver vide.
    if (!blitVignette(g, ch, currentWorld, x, y, size)) {
      // Tuiles STRUCTURELLES rendues dans le style du MONDE sélectionné (retour Claude-Alain 16/07) :
      // brique/fausse brique/béton/sol friable/rampes suivent currentWorld, comme en jeu. La palette
      // étant reconstruite au changement de monde (onWorldChange), ses vignettes suivent aussi.
      const wt = WORLD_TILES[currentWorld]
      const sr = ch === '#' || ch === 'x' ? wt.brick
        : ch === '=' ? wt.concrete
          : ch === 'c' ? wt.crumble
            : ch === '/' ? wt.rampR
              : ch === '\\' ? wt.rampL
                : baseSprite(ch)
      // Bâtisseur / statue (17/07) : mêmes frames que le moine normal, distinguées par un FILTRE
      // (ocre / pierre) — exactement le traitement du jeu (drawTinted / greySprite). L'éditeur
      // tourne sur desktop/tablette : ctx.filter y est fiable ; au pire, dégradation en violet.
      if (ch === 'µ') g.filter = 'hue-rotate(120deg) saturate(1.5)'
      else if (ch === '`') g.filter = 'grayscale(1) brightness(0.85)'
      if (sr) g.drawImage(sheet, sr.sx, sr.sy, 16, 16, x, y, size, size)
      g.filter = 'none'
    }
    if (ch === 'x') {
      // Fausse brique : marqueur pour la distinguer d'une vraie brique (sprite identique).
      g.fillStyle = 'rgba(255, 210, 63, 0.92)'
      g.font = `bold ${Math.round(size * 0.4)}px ${FONT}`
      g.textAlign = 'left'; g.textBaseline = 'top'
      g.fillText('x', x + 2, y + 1)
    } else if (TP_CHARS.includes(ch)) {
      // Lettre de la paire, en gold sur ombre sombre (lisible par-dessus le sprite).
      g.textAlign = 'center'; g.textBaseline = 'middle'
      g.font = `bold ${Math.round(size * 0.5)}px ${FONT}`
      g.fillStyle = '#0d0b1a'
      g.fillText(ch, x + size / 2 + 1, y + size / 2 + 1.5)
      g.fillStyle = '#ffd23f'
      g.fillText(ch, x + size / 2, y + size / 2)
    } else if (ch === 'Z') {
      // Zone mortelle : INVISIBLE en jeu (piège), mais marquée d'une CROIX ROUGE dans l'ÉDITEUR
      // (mode créatif) pour que le créateur voie où il en a posé. Ce marqueur ne vit que dans editor.ts.
      const p = size * 0.24
      g.save()
      g.fillStyle = 'rgba(255, 45, 45, 0.18)'
      g.fillRect(x + 1, y + 1, size - 2, size - 2)
      g.strokeStyle = 'rgba(255, 80, 80, 0.95)'
      g.lineWidth = Math.max(2, size * 0.09)
      g.lineCap = 'round'
      g.beginPath()
      g.moveTo(x + p, y + p); g.lineTo(x + size - p, y + size - p)
      g.moveTo(x + size - p, y + p); g.lineTo(x + p, y + size - p)
      g.stroke()
      g.restore()
    } else if (CRATE_CONTENT[ch]) {
      // Caisse bois : BADGE du contenu (coin bas-droite) dans l'ÉDITEUR seulement. Le jeu cache le
      // contenu (surprise à la casse) ; ici ça aide le créateur à distinguer ses caisses d'un coup d'œil.
      // En papier (29/07) : le badge est l'objet lui-même, réduit dans le coin.
      const bs = size * 0.5
      const objet = CRATE_BADGE[ch]
      if (objet) blitObjet(g, objet, bs, x + size - bs - 1, y + size - bs - 1)
      else { const cs = CRATE_CONTENT[ch]!
        g.drawImage(sheet, cs.sx, cs.sy, 16, 16, x + size - bs - 1, y + size - bs - 1, bs, bs) }
    }
  }

  function redraw(): void {
    const W = canvas.width, H = canvas.height
    ctx.fillStyle = '#0d0b1a'
    ctx.fillRect(0, 0, W, H)
    // Grille : rend les cases vides visibles pour construire. Sur une GRANDE carte, le canvas (jusqu'à
    // 1792×1024) est fortement réduit par le CSS -> une ligne d'1 px interne devient sous-pixel et
    // DISPARAÎT. On compense : épaisseur ~1 px À L'ÉCRAN (échelle d'affichage réelle = « contain ») +
    // opacité relevée, pour un quadrillage net quelle que soit la taille de la carte.
    const cw = canvas.clientWidth, chh = canvas.clientHeight
    const dispScale = cw > 0 && chh > 0 ? Math.min(cw / W, chh / H) : 1 // px écran / px interne
    ctx.strokeStyle = 'rgba(240, 233, 210, 0.18)'
    ctx.lineWidth = Math.max(1, 1 / dispScale)
    ctx.beginPath()
    for (let c = 0; c <= edCols; c++) { const x = c * ED_CELL + 0.5; ctx.moveTo(x, 0); ctx.lineTo(x, H) }
    for (let r = 0; r <= edRows; r++) { const y = r * ED_CELL + 0.5; ctx.moveTo(0, y); ctx.lineTo(W, y) }
    ctx.stroke()
    for (let r = 0; r < edRows; r++) {
      const line = rows[r]!
      for (let c = 0; c < edCols; c++) {
        const ch = line[c]!
        if (ch === '.' || ch === '_') continue // '_' : le VER complet est dessiné plus bas (5 cases)
        // Or/objet IMMERGÉ (16/07 nuit) : même règle que le parse (eau au-dessus -> l'eau coule
        // sur la case) — le créateur voit le fond d'eau sous sa pièce, pas un trou dans le bassin.
        if ('$bjzkr'.includes(ch) && r > 0 && '~:,;'.includes(rows[r - 1]![c]!)) {
          const w = TILE_SPRITES[T.Water]!
          ctx.drawImage(sheet, w.sx, w.sy, 16, 16, c * ED_CELL, r * ED_CELL, ED_CELL, ED_CELL)
        }
        drawCellVisual(ctx, ch, c * ED_CELL, r * ED_CELL, ED_CELL)
      }
    }
    // VERS CHECKPOINT : le ver COMPLET (5 cases, version dormante) depuis chaque ancre '_' — le
    // créateur voit l'emprise réelle de la zone d'activation. Point doré = la case ANCRE (celle
    // qu'il faut effacer pour retirer le ver). Hors-bornes clippé, comme en jeu.
    for (let r = 0; r < edRows; r++) {
      const line = rows[r]!
      for (let c = 0; c < edCols; c++) {
        if (line[c] !== '_') continue
        for (let i = 0; i < 5; i++) {
          const cc = c + i - 2
          if (cc < 0 || cc >= edCols) continue
          const sr = WORM_SLEEP[i]!
          ctx.drawImage(sheet, sr.sx, sr.sy, 16, 16, cc * ED_CELL, r * ED_CELL, ED_CELL, ED_CELL)
        }
        ctx.fillStyle = '#ffd23f'
        ctx.fillRect(c * ED_CELL + ED_CELL / 2 - 2, r * ED_CELL + ED_CELL - 5, 4, 3) // marqueur d'ancre
      }
    }
    // Couche DÉCO en surimpression (par-dessus le terrain) : le créateur voit les deux couches.
    for (let r = 0; r < edRows; r++) {
      const line = decoRows[r]!
      for (let c = 0; c < edCols; c++) {
        const ch = line[c]!
        if (ch !== '.' && !BIG_BY_CHAR.has(ch)) drawCellVisual(ctx, ch, c * ED_CELL, r * ED_CELL, ED_CELL)
      }
    }
    // GRANDS DÉCORS (17/07) : l'ensemble COMPLET depuis chaque ancre (emprise réelle visible),
    // point doré = la case ancre (celle à gommer pour retirer l'ensemble). Figé (t=0).
    for (let r = 0; r < edRows; r++) {
      const line = decoRows[r]!
      for (let c = 0; c < edCols; c++) {
        const k = BIG_BY_CHAR.get(line[c]!)
        if (!k) continue
        const b = BIG_DECOS[k]
        ctx.save()
        ctx.translate((c - b.ax) * ED_CELL, (r - b.ay) * ED_CELL)
        ctx.scale(ED_CELL / 16, ED_CELL / 16)
        drawBigDeco(ctx, k, 0, 0, 0)
        ctx.restore()
        ctx.fillStyle = '#ffd23f'
        ctx.fillRect(c * ED_CELL + ED_CELL / 2 - 2, r * ED_CELL + ED_CELL - 5, 4, 3)
      }
    }
    if (hoverCell >= 0) {
      ctx.strokeStyle = '#ffd23f'
      ctx.lineWidth = 2
      ctx.strokeRect(colOf(hoverCell, edCols) * ED_CELL + 1, rowOf(hoverCell, edCols) * ED_CELL + 1, ED_CELL - 2, ED_CELL - 2)
    }
  }

  // --- édition de la grille ------------------------------------------------
  function countChar(target: string): number {
    let n = 0
    for (const line of rows) for (const ch of line) if (ch === target) n++
    return n
  }
  // Total de moines TOUS TYPES confondus (m + S + G + M) pour le plafond MAX_MONKS, cohérent
  // avec validateLevel côté moteur (qui borne monkSpawns.length, tous types mêlés).
  function countMonks(): number {
    let n = 0
    for (const ch of MONK_CHARS) n += countChar(ch)
    return n
  }
  function clearChar(target: string): void {
    for (let r = 0; r < edRows; r++) {
      const line = rows[r]!
      if (line.includes(target)) rows[r] = line.split(target).join('.')
    }
  }
  function setCell(cell: number, ch: string): void {
    const r = rowOf(cell, edCols), c = colOf(cell, edCols)
    const line = rows[r]!
    if (line[c] === ch) return
    rows[r] = line.slice(0, c) + ch + line.slice(c + 1)
  }
  function place(cell: number, ch: string): void {
    if (ch === '1' || ch === '2' || ch === '3') { clearChar(ch); setCell(cell, ch); return } // singleton : un seul spawn par joueur
    if (MONK_CHARS.includes(ch)) {
      const existing = rows[rowOf(cell, edCols)]![colOf(cell, edCols)] // string | undefined (noUncheckedIndexedAccess)
      if (existing === ch) return // déjà ce type de moine sur la case : rien à faire
      // Plafond MAX_MONKS tous types confondus. Remplacer un moine par un AUTRE type ne change
      // pas le total : on ne bloque que l'ajout d'un moine sur une case qui n'en portait pas.
      const replacingMonk = existing !== undefined && MONK_CHARS.includes(existing)
      if (!replacingMonk && countMonks() >= MAX_MONKS) {
        showStatus(`${MAX_MONKS} moines au maximum.`, 'warn'); return
      }
      setCell(cell, ch); return
    }
    setCell(cell, ch)
  }
  function placeTeleporter(cell: number): void {
    const pair = TP_PAIRS[selected]
    if (!pair) return
    const idx = tpNext[selected] ?? 0
    const ch = pair[idx]!
    clearChar(ch) // une seule entrée et une seule sortie par paire
    setCell(cell, ch)
    tpNext[selected] = idx === 0 ? 1 : 0 // prochaine pose = l'autre extrémité
  }
  // Couche DÉCO : pose/efface un caractère de déco sur `decoRows` SANS toucher le terrain dessous.
  function setDeco(cell: number, ch: string): void {
    const r = rowOf(cell, edCols), c = colOf(cell, edCols)
    const line = decoRows[r]!
    if (line[c] === ch) return
    decoRows[r] = line.slice(0, c) + ch + line.slice(c + 1)
  }
  // La couche active découle du caractère sélectionné : un char de déco (ou la gomme déco) agit
  // sur la couche DÉCO ; tout le reste agit sur la couche TERRAIN (comportement historique).
  function selectedIsDeco(): boolean { return DECO_CHARS.has(selected) || selected === DECO_ERASE }
  function applyAt(cell: number): void {
    if (selectedIsDeco()) {
      // clic droit (gomme) OU gomme déco -> efface la déco ; sinon pose la déco (terrain intact).
      setDeco(cell, eraseMode || selected === DECO_ERASE ? '.' : selected)
      return
    }
    if (eraseMode) { setCell(cell, '.'); return }
    if (TP_CHARS.includes(selected)) { placeTeleporter(cell); return }
    place(cell, selected)
  }

  // --- pointeur (souris + tactile, Phase 3 L1) -----------------------------
  // Migration MouseEvent -> PointerEvent : un SEUL pointeur peint à la fois (activePointerId ;
  // L2 gérera le 2e doigt pour le zoom/pan). Souris : clic gauche dessine, clic droit gomme,
  // survol met en évidence. Tactile : le doigt dessine ; la bascule Gomme (#ed-erase) remplace
  // le clic droit. PointerEvent étend MouseEvent -> cellFromEvent lit clientX/Y à l'identique.
  function cellFromEvent(e: MouseEvent): number {
    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return -1
    // Le canvas est affiché en `object-fit: contain` (letterbox CENTRÉ) : le buffer n'occupe pas toute
    // la boîte `rect` quand les ratios diffèrent (typiquement sur tablette, où la scène est bordée à
    // gauche/droite). Il FAUT mapper le clic via l'échelle « contain » (min des deux axes) et l'offset
    // de centrage, sinon le placement se décale d'autant plus qu'on s'éloigne du centre (bug tablette).
    // Marche aussi SANS letterbox (offset = 0) et sous le transform de zoom/pan (rect est post-transform,
    // donc scale/offset le sont aussi -> cohérent).
    const scale = Math.min(rect.width / canvas.width, rect.height / canvas.height)
    const offX = (rect.width - canvas.width * scale) / 2, offY = (rect.height - canvas.height * scale) / 2
    const px = (e.clientX - rect.left - offX) / scale, py = (e.clientY - rect.top - offY) / scale
    const c = Math.floor(px / ED_CELL), r = Math.floor(py / ED_CELL)
    if (c < 0 || c >= edCols || r < 0 || r >= edRows) return -1
    return cellIndex(c, r, edCols)
  }
  // --- zoom / déplacement (Phase 3 L2, cosmétique) -------------------------
  // La vue s'applique au canvas via transform CSS (origine 0 0). cellFromEvent reste correct sous le
  // zoom/pan : `rect` (getBoundingClientRect) est POST-transform, donc son échelle « contain » et son
  // offset de centrage le sont aussi -> le mapping clic->case suit la vue sans traitement supplémentaire.
  function applyTransform(): void {
    const identity = view.scale === 1 && view.tx === 0 && view.ty === 0
    canvas.style.transformOrigin = '0 0'
    canvas.style.transform = identity ? '' : `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`
    recenterBtn.disabled = identity // rien à recentrer à la vue naturelle
  }
  // Dimensions CSS NATURELLES du canvas (offset*, indépendantes du transform) = cadre de référence du clamp.
  function naturalSize(): { w: number; h: number } {
    return { w: canvas.offsetWidth || canvas.width, h: canvas.offsetHeight || canvas.height }
  }
  function startGesture(): void {
    const ids = [...pointers.keys()].slice(0, 2)
    const a = pointers.get(ids[0]!)!, b = pointers.get(ids[1]!)!
    // Passage à 2 doigts : annule proprement un trait commencé par le 1er doigt (pas de tuile fantôme,
    // et restaure tpNext -> pas de paire de téléporteur cassée si le trait interrompu en posait une).
    if (painting && strokeBefore) { rows = strokeBefore.rows.slice(); decoRows = strokeBefore.deco.slice(); Object.assign(tpNext, strokeBefore.tp) }
    painting = false; activePointerId = -1; strokeBefore = null; lastCell = -1; hoverCell = -1
    const rect = canvas.getBoundingClientRect()
    const nlx = rect.left - view.tx, nly = rect.top - view.ty // coin haut-gauche NATUREL du canvas (écran)
    const m = midpoint(a.x, a.y, b.x, b.y)
    gesture = {
      ids: [ids[0]!, ids[1]!], startView: { ...view }, startDist: dist(a.x, a.y, b.x, b.y),
      startMidX: m.x, startMidY: m.y, focusLX: m.x - nlx, focusLY: m.y - nly,
    }
    redraw() // retire l'encadré de survol pendant le geste
  }
  function updateGesture(): void {
    if (!gesture) return
    const a = pointers.get(gesture.ids[0]), b = pointers.get(gesture.ids[1])
    if (!a || !b) return
    const ratio = gesture.startDist > 0 ? dist(a.x, a.y, b.x, b.y) / gesture.startDist : 1
    const m = midpoint(a.x, a.y, b.x, b.y)
    let v = pinch(gesture.startView, gesture.focusLX, gesture.focusLY, ratio) // zoom, focal fixe
    v = pan(v, m.x - gesture.startMidX, m.y - gesture.startMidY)               // + glissé des 2 doigts
    const ns = naturalSize()
    view = clampView(v, ns.w, ns.h)
    applyTransform()
  }
  function endGesture(): void { gesture = null }
  // Molette souris (desktop) : zoom centré sur le curseur (focal = position locale naturelle du curseur).
  function onWheel(e: WheelEvent): void {
    e.preventDefault()
    const rect = canvas.getBoundingClientRect()
    const ns = naturalSize()
    const ratio = e.deltaY < 0 ? 1.15 : 1 / 1.15
    view = clampView(pinch(view, e.clientX - (rect.left - view.tx), e.clientY - (rect.top - view.ty), ratio), ns.w, ns.h)
    applyTransform()
  }
  function recenter(): void { view = { scale: 1, tx: 0, ty: 0 }; applyTransform() } // revient à la vue naturelle
  // Zoom initial intelligent (audit 16/07, C2) : sur TACTILE et GRANDE carte (> 28x16),
  // letterboxer toute la grille rend l'édition illisible sur téléphone. Si du contenu est déjà
  // construit, on cadre d'emblée sa zone (même géométrie que le geste 2 doigts : pinch focal
  // borné + clamp) ; carte vide, carte standard ou desktop : vue naturelle inchangée. ◎ et le
  // pinch restent les moyens normaux de reprendre la main (le cadrage n'est qu'un point de départ).
  function initialZoom(): void {
    if (!isTouch || (edCols <= 28 && edRows <= 16)) return
    let c0 = edCols, r0 = edRows, c1 = -1, r1 = -1
    const scan = (g: string[]): void => {
      g.forEach((line, r) => {
        for (let c = 0; c < line.length; c++) {
          if (line[c] !== '.') {
            if (c < c0) c0 = c
            if (c > c1) c1 = c
            if (r < r0) r0 = r
            if (r > r1) r1 = r
          }
        }
      })
    }
    scan(rows)
    scan(decoRows)
    if (c1 < 0) return // carte vierge : letterbox naturel (on choisit où construire, puis on zoome)
    // Fraction de carte couverte (1 case de marge de chaque côté) -> ratio, borné comme le pinch.
    const fw = Math.min(edCols, c1 - c0 + 3) / edCols, fh = Math.min(edRows, r1 - r0 + 3) / edRows
    const ratio = Math.min(6, 1 / Math.max(fw, fh))
    if (ratio <= 1.15) return // le contenu occupe déjà presque toute la carte : rien à cadrer
    requestAnimationFrame(() => { // après layout : le rect letterboxé du canvas est fiable
      const rect = canvas.getBoundingClientRect()
      if (rect.width === 0 || view.scale !== 1 || view.tx !== 0 || view.ty !== 0) return // invisible, ou vue déjà manipulée
      // Centre du contenu en px écran NATURELS : même mapping « contain » que cellFromEvent.
      const sc = Math.min(rect.width / canvas.width, rect.height / canvas.height)
      const offX = (rect.width - canvas.width * sc) / 2, offY = (rect.height - canvas.height * sc) / 2
      const fx = offX + ((c0 + c1 + 1) / 2) * ED_CELL * sc
      const fy = offY + ((r0 + r1 + 1) / 2) * ED_CELL * sc
      const ns = naturalSize()
      view = clampView(pinch(view, fx, fy, ratio), ns.w, ns.h)
      applyTransform()
    })
  }
  // Plein écran de dessin (audit 16/07, C1) : masque barre + palette (classe CSS .ed-zen sur
  // #editor), la scène prend tout ; ✕ flottant pour ressortir. redraw() recale l'épaisseur du
  // quadrillage sur la nouvelle échelle d'affichage. État TOUJOURS annulé au détachement.
  function setZen(on: boolean): void {
    root.classList.toggle('ed-zen', on)
    zenBtn.setAttribute('aria-pressed', String(on))
    redraw()
  }
  function onZenOn(): void { setZen(true) }
  function onZenOff(): void { setZen(false) }

  // --- pointeur (peinture 1 doigt / zoom-pan 2 doigts) ---------------------
  function onPointerDown(e: PointerEvent): void {
    // Tiroir Réglages ouvert (mobile) : un tap sur le canvas le referme sans dessiner (dismiss).
    if (settingsPanel.classList.contains('is-open')) { e.preventDefault(); onSettingsToggle(); return }
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 2) return // souris : ni molette ni bouton latéral
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.size >= 2) { e.preventDefault(); if (!gesture) startGesture(); return } // 2 doigts -> zoom/pan
    if (painting) return // un pointeur peint déjà (défensif)
    const cell = cellFromEvent(e)
    if (cell < 0) return // hors grille : pointeur suivi (pour un éventuel 2e doigt) mais aucun trait
    e.preventDefault()
    activePointerId = e.pointerId
    try { canvas.setPointerCapture(e.pointerId) } catch { /* capture best-effort (canvas partagé) */ }
    painting = true
    eraseMode = eraseToggle || (e.pointerType === 'mouse' && e.button === 2) // bascule Gomme OU clic droit
    strokeBefore = snapshot()
    applyAt(cell)
    lastCell = cell
    hoverCell = cell
    redraw()
  }
  function onPointerMove(e: PointerEvent): void {
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (gesture) { updateGesture(); return } // 2 doigts : zoom/pan, pas de peinture
    const cell = cellFromEvent(e)
    if (painting && e.pointerId === activePointerId) {
      // Téléporteur (hors gomme) = pose discrète (tap par tap), pas de peinture au glisser.
      const discrete = !eraseMode && TP_CHARS.includes(selected)
      if (cell >= 0 && cell !== lastCell && !discrete) { applyAt(cell); lastCell = cell }
    }
    // Survol : ne suit QUE le pointeur actif pendant un trait (sinon un 2e doigt ferait sauter l'encadré).
    if ((!painting || e.pointerId === activePointerId) && cell !== hoverCell) { hoverCell = cell; redraw() }
  }
  function endStroke(): void {
    if (!painting) return
    painting = false
    activePointerId = -1
    // Un seul état d'historique par trait : on empile l'état (2 couches) AVANT le trait s'il a changé.
    if (strokeBefore && !sameSnap(strokeBefore, snapshot())) {
      undoStack.push(strokeBefore)
      if (undoStack.length > MAX_HISTORY) undoStack.shift()
      redoStack.length = 0
    }
    strokeBefore = null
    lastCell = -1
    refreshToolButtons()
  }
  function onPointerUp(e: PointerEvent): void {
    pointers.delete(e.pointerId)
    if (gesture) {
      // Fin du geste dès qu'un des DEUX doigts du geste se lève (et pas seulement quand il reste < 2
      // contacts : un 3e doigt parasite figerait sinon le zoom). On repart aussitôt si ≥ 2 doigts restent.
      if (gesture.ids.includes(e.pointerId)) { endGesture(); if (pointers.size >= 2) startGesture() }
      return
    }
    if (e.pointerId !== activePointerId) return // up/cancel d'un autre pointeur : rien à finir
    endStroke()
  }
  function onPointerLeave(): void { if (!painting && hoverCell !== -1) { hoverCell = -1; redraw() } }
  function onContextMenu(e: Event): void { e.preventDefault() } // le clic droit gomme, pas de menu

  // --- annuler / refaire ---------------------------------------------------
  function undo(): void {
    const prev = undoStack.pop()
    if (!prev) return
    redoStack.push(snapshot())
    rows = prev.rows; decoRows = prev.deco; Object.assign(tpNext, prev.tp) // restaure aussi l'alternance téléporteur
    redraw()
    refreshToolButtons()
  }
  function redo(): void {
    const next = redoStack.pop()
    if (!next) return
    undoStack.push(snapshot())
    rows = next.rows; decoRows = next.deco; Object.assign(tpNext, next.tp)
    redraw()
    refreshToolButtons()
  }
  // Outils flottants (Phase 3 L1) : reflète l'état de l'historique (boutons grisés si vide) et
  // de la bascule Gomme. Appelé après chaque trait, annuler/refaire, redimensionnement et au montage.
  function refreshToolButtons(): void {
    undoBtn.disabled = undoStack.length === 0
    redoBtn.disabled = redoStack.length === 0
    eraseBtn.classList.toggle('is-on', eraseToggle)
    eraseBtn.setAttribute('aria-pressed', eraseToggle ? 'true' : 'false')
  }
  function onUndoClick(): void { undo() }
  function onRedoClick(): void { redo() }
  function onEraseClick(): void { eraseToggle = !eraseToggle; refreshToolButtons() } // gomme au doigt (pas de clic droit)
  function onKey(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
    if (!(e.metaKey || e.ctrlKey)) return
    const k = e.key.toLowerCase()
    if (k === 'z' && !e.shiftKey) { e.preventDefault(); undo() }
    else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); redo() }
  }

  // --- barre d'actions -----------------------------------------------------
  function showStatus(msg: string, kind: 'info' | 'success' | 'error' | 'warn'): void {
    statusEl.textContent = msg // SÉCURITÉ : textContent (jamais innerHTML) pour les messages serveur
    statusEl.className = 'ed-status is-' + kind
  }
  function handleTest(): void {
    if (testHandle) return // déjà en test (défensif : les boutons sont masqués pendant la partie)
    // Pré-vol : la carte doit être valide (mêmes règles que le serveur) avant de lancer.
    const name = nameInput.value.trim() || 'Essai'
    let problems: string[]
    try {
      problems = validateLevel(levelFromAscii(name, rows))
    } catch (e) {
      showStatus('Carte invalide : ' + (e instanceof Error ? e.message : String(e)), 'error')
      return
    }
    if (problems.length > 0) {
      showStatus('Corrige avant de tester : ' + problems.join(', '), 'error')
      return
    }
    // Carte jouable : on bascule le canvas partagé en mode jeu et on lance le solo.
    // - slug : « fam-<id> » si la carte est enregistrée (le serveur autorise l'auteur à
    //   scorer sa propre carte, même brouillon), sinon « preview » (slug NON reconnu par le
    //   serveur : aucun score possible pour un brouillon jamais enregistré).
    // - onLevelWon : on ne pose QUE le badge « fini » (idempotent côté serveur) pour une
    //   carte enregistrée. CHOIX ASSUMÉ : on N'ENVOIE PAS de score depuis « Tester » — les
    //   scores se gagnent en jouant la carte depuis la bibliothèque (Task 7) ; « Tester »
    //   sert à vérifier la jouabilité, pas à alimenter le classement. Pour un brouillon
    //   « preview », la victoire n'affiche que l'écran « Bravo » (aucun appel réseau).
    const slug = currentId !== null ? `fam-${currentId}` : 'preview'
    detachEditorListeners() // les raccourcis (annuler/refaire) et la souris ne doivent pas jouer pendant la partie
    root.hidden = true // masque tout l'éditeur (barre + palette) : le canvas devient le jeu
    useGameCanvas()
    // rows.slice() : le test reçoit une COPIE (l'éditeur garde ses lignes intactes). Les
    // chaînes étant immuables, cette copie de surface suffit à isoler l'état de l'éditeur.
    testHandle = startSolo({
      canvas, sheet,
      levels: [{ slug, title: name, rows: rows.slice(), world: currentWorld, monkSpeed: currentMonkSpeed, smartMonks: currentSmartMonks, monkPowers: currentMonkPowers, decor: decoRows.join('\n') }],
      startIndex: 0,
      onQuit: reprendreEditeur,
      onLevelWon: () => { if (currentId !== null) void apiFinishedLevel(currentId).catch(() => {}) },
    })
  }
  function reprendreEditeur(): void {
    // Retour depuis la partie de test (Échap, game over ou victoire) : le solo s'est déjà
    // démonté. On rend le canvas à l'éditeur, on réarme les écouteurs et on redessine tout.
    testHandle = null
    if (stopped) return // l'éditeur a été fermé entre-temps (non atteignable via l'UI, défensif)
    useEditorCanvas()
    root.hidden = false
    attachEditorListeners()
    hoverCell = -1
    redraw()
    showStatus('Retour à l\'éditeur. Continue ta carte, puis enregistre-la.', 'info')
  }
  async function handleSave(): Promise<void> {
    const name = nameInput.value.trim()
    if (name.length === 0) { showStatus('Donne un nom à ta carte.', 'error'); nameInput.focus(); return }
    saveBtn.disabled = true
    showStatus('Enregistrement...', 'info')
    try {
      // Fige l'ascii ENVOYÉ avant l'await : le canvas reste peignable pendant la requête
      // réseau ; sans ce gel, un trait tracé pendant l'envoi serait faussement considéré
      // comme sauvegardé (savedAscii lu sur rows vivant) et perdu en silence au Quitter.
      const ascii = rows.join('\n')
      const decor = decoRows.join('\n')                  // grille déco courante (réf anti-perte)
      const decorToSend = toDecorString(decor)           // normalisée avant envoi ('' si vide)
      const draft = { name, ascii, world: currentWorld, monkSpeed: currentMonkSpeed, smartMonks: currentSmartMonks, monkPowers: currentMonkPowers, decor: decorToSend }
      if (officielSlug !== null) {
        // CARTE OFFICIELLE (admin) : on écrase la version servie à toute la famille. Le serveur
        // refuse une grille invalide — c'est volontaire, cette fonction sert à RÉPARER une carte,
        // pas à en casser une bonne.
        await apiPutOfficialLevel(officielSlug, ascii, currentWorld, currentMonkSpeed,
                                  currentSmartMonks, currentMonkPowers, decorToSend)
        showStatus('Carte officielle mise à jour pour toute la famille.', 'success')
      } else if (currentLocalId !== null) {
        // Carte créée sans réseau et pas encore publiée : on met à jour le BROUILLON, sans réseau.
        // On peut donc enregistrer autant de fois qu'on veut au chalet — il n'en partira qu'une.
        saveDraft(localStorage, { ...draft, localId: currentLocalId, at: Date.now() })
        showStatus('Enregistré sur l’appareil. Ta carte partira au retour du réseau.', 'success')
      } else if (currentId === null) {
        try {
          const created: LevelFull = await apiCreateLevel(name, ascii, currentWorld, currentMonkSpeed, currentSmartMonks, decorToSend, currentMonkPowers)
          currentId = created.id // les enregistrements suivants modifient cette carte
          nameInput.value = created.name
          // Toutes les cartes sont visibles par la famille dès l'enregistrement (plus de
          // brouillon ni d'étape « Publier ») : on le dit clairement à l'auteur.
          showStatus('Enregistré ! Ta carte est visible par tous.', 'success')
        } catch (e) {
          // PAS DE RÉSEAU (25/07) : plutôt que de perdre le travail, la carte est rangée sur
          // l'appareil et publiée au retour. Une erreur de VALIDATION (grille refusée), elle, doit
          // rester visible : la ranger silencieusement ferait échouer la publication plus tard,
          // loin de l'éditeur, sans que l'auteur puisse corriger.
          if (e instanceof ApiError && e.status !== null) throw e
          currentLocalId = newLocalId(Date.now())
          saveDraft(localStorage, { ...draft, localId: currentLocalId, at: Date.now() })
          showStatus('Pas de réseau : carte gardée sur l’appareil, elle partira au retour.', 'success')
        }
      } else {
        await apiUpdateLevel(currentId, name, ascii, currentWorld, currentMonkSpeed, currentSmartMonks, decorToSend, currentMonkPowers)
        showStatus('Enregistré.', 'success')
      }
      savedAscii = ascii // référence anti-perte = ce qui a réellement été envoyé
      savedWorld = currentWorld // idem pour le monde (le Quitter ne préviendra plus pour ce champ)
      savedMonkSpeed = currentMonkSpeed // idem pour la vitesse des moines
      savedSmartMonks = currentSmartMonks // idem pour l'IA maligne
      savedMonkPowers = currentMonkPowers // idem pour les moines armés
      savedDecor = decor // idem pour la couche déco
    } catch (err) {
      showStatus(err instanceof Error && err.message ? err.message : 'Une erreur est survenue. Réessaie.', 'error')
    } finally {
      saveBtn.disabled = false
    }
  }
  function onSaveClick(): void { void handleSave() }
  function handleQuit(): void {
    // Garde anti-perte de carte : prévenir si des modifications ne sont pas enregistrées.
    // « Tester » ne passe PAS par ici (on reste dans l'éditeur) ; seul Quitter est gardé.
    if (
      (rows.join('\n') !== savedAscii || currentWorld !== savedWorld || currentMonkSpeed !== savedMonkSpeed || currentSmartMonks !== savedSmartMonks || currentMonkPowers !== savedMonkPowers || decoRows.join('\n') !== savedDecor) &&
      !confirm('Des modifications ne sont pas enregistrées. Quitter quand même ?')
    ) {
      return
    }
    teardown(); opts.onQuit()
  }

  // --- palette -------------------------------------------------------------
  function drawThumb(thumb: HTMLCanvasElement, ch: string): void {
    const g = thumb.getContext('2d')!
    g.imageSmoothingEnabled = false
    g.fillStyle = '#100d26'
    g.fillRect(0, 0, thumb.width, thumb.height)
    if (ch === '.' || ch === DECO_ERASE) {
      // Gomme (terrain ou déco) : case vide au bord pointillé.
      g.strokeStyle = '#6f6a90'
      g.setLineDash([3, 3]); g.lineWidth = 1
      g.strokeRect(4.5, 4.5, thumb.width - 9, thumb.height - 9)
      g.setLineDash([])
      return
    }
    drawCellVisual(g, ch, 0, 0, thumb.width)
  }
  function setSelected(ch: string): void {
    selected = ch
    for (const btn of palette.querySelectorAll<HTMLButtonElement>('.pal-btn')) {
      btn.classList.toggle('is-active', btn.dataset.char === ch)
    }
  }
  // Groupe « Décorations » DYNAMIQUE : socle universel (biome 'all') + signatures du biome
  // courant + la gomme déco. Reconstruit quand le monde change (onWorldChange -> buildPalette).
  function decoGroup(): { title: string; tiles: Tile[] } {
    const tiles: Tile[] = (Object.keys(DECOS) as DecoKind[])
      .filter((k) => !k.startsWith('tresor') && (DECOS[k].biome === 'all' || DECOS[k].biome === currentWorld))
      .map((k) => ({ ch: DECOS[k].ch, label: DECOS[k].name }))
    // GRANDS DÉCORS (17/07) : les ensembles multi-cases du carnet, posés par leur ancre.
    for (const k of Object.keys(BIG_DECOS) as BigDecoKind[]) {
      const b = BIG_DECOS[k]
      if (b.biome === 'all' || b.biome === currentWorld) tiles.push({ ch: b.ch, label: `${b.name} (${b.w}\u00d7${b.h})` })
    }
    return { title: 'Décorations', tiles }
  }
  // Onglet actif (Phase 3 L3) : au tactile, un seul panneau visible ; au desktop, tous empilés
  // (le CSS `pointer: fine` ignore l'état) -> ce toggle est cosmétique côté souris, pilotant côté doigt.
  function setActiveGroup(title: string): void {
    activeGroupTitle = title
    for (const t of palette.querySelectorAll<HTMLElement>('.pal-tab')) {
      const on = t.dataset.group === title
      t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on ? 'true' : 'false')
    }
    for (const p of palette.querySelectorAll<HTMLElement>('.pal-panel')) p.classList.toggle('is-active', p.dataset.group === title)
  }
  /**
   * Fabrique un bouton de palette. Extrait de `buildPalette` le 06/08 pour que la barre des
   * gommes et les panneaux d'onglets peignent EXACTEMENT le même bouton — deux fabriques
   * séparées auraient divergé au premier changement de style.
   */
  function tileButton(tile: Tile): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'pal-btn'
    btn.dataset.char = tile.ch
    btn.title = tile.label
    const thumb = document.createElement('canvas')
    thumb.className = 'pal-thumb'
    thumb.width = 32; thumb.height = 32
    drawThumb(thumb, tile.ch)
    const label = document.createElement('span')
    label.className = 'pal-label'
    label.textContent = tile.label
    btn.append(thumb, label)
    // Choisir une tuile sort de la Gomme (Phase 3 L1) : on vient de dire QUOI poser, on ne
    // veut pas continuer à effacer. Sans quoi la bascule Gomme resterait un piège au doigt.
    btn.addEventListener('click', () => {
      if (eraseToggle) { eraseToggle = false; refreshToolButtons() }
      setSelected(tile.ch)
    })
    return btn
  }

  /**
   * LES GOMMES, TOUJOURS EN HAUT (06/08, demande de Claude-Alain : « déplace les gommes dans le
   * mode création pour qu'elles soient toujours affichées en haut »).
   *
   * Elles vivaient chacune dans son onglet — le vide en tête de « Terrain », la gomme déco en
   * QUEUE de « Décorations ». Effacer demandait donc de quitter l'onglet où l'on travaillait,
   * puis d'y revenir : le geste le plus fréquent du dessin était le plus cher de la palette.
   * Elles sortent des onglets et prennent une barre à elles, au-dessus, visible quoi qu'on peigne.
   */
  const GOMMES: readonly Tile[] = [
    { ch: '.', label: 'Gomme' },
    { ch: DECO_ERASE, label: 'Gomme déco' },
  ]

  function buildPalette(): void {
    palette.replaceChildren() // réinitialise (l'overlay est réutilisé d'une ouverture à l'autre)
    const barreGommes = document.createElement('div')
    barreGommes.className = 'pal-gommes'
    for (const g of GOMMES) barreGommes.appendChild(tileButton(g))
    // Déco disponible à TOUTES les tailles (decor.ts migré Lot E : dimensions déduites de la grille).
    // Groupe « Trésors » (21/07) : les 20 trésors-décos (codes 32+), à part pour ne pas noyer les
    // décorations classiques — la salle au trésor se peint d'un bloc.
    const tresorGroup = {
      title: 'Trésors',
      tiles: (Object.keys(DECOS) as DecoKind[])
        .filter((k) => k.startsWith('tresor'))
        .map((k) => ({ ch: DECOS[k].ch, label: DECOS[k].name })),
    }
    const groups = [...GROUPS, decoGroup(), tresorGroup]
    const tabs = document.createElement('div')
    tabs.className = 'pal-tabs'; tabs.setAttribute('role', 'tablist')
    const panels = document.createElement('div')
    panels.className = 'pal-panels'
    for (const group of groups) {
      const tab = document.createElement('button')
      tab.type = 'button'; tab.className = 'pal-tab'; tab.dataset.group = group.title
      tab.setAttribute('role', 'tab')
      tab.textContent = group.title // libellés STATIQUES (aucune origine utilisateur)
      tab.addEventListener('click', () => setActiveGroup(group.title))
      tabs.appendChild(tab)
      const panel = document.createElement('div')
      panel.className = 'pal-panel'; panel.dataset.group = group.title
      panel.setAttribute('role', 'tabpanel')
      const title = document.createElement('div')
      title.className = 'pal-group-title'; title.textContent = group.title // visible au desktop, masqué au mobile (l'onglet le porte)
      const grid = document.createElement('div')
      grid.className = 'pal-grid'
      for (const tile of group.tiles) grid.appendChild(tileButton(tile))
      panel.append(title, grid)
      panels.appendChild(panel)
    }
    palette.append(barreGommes, tabs, panels)
    setSelected(selected)
    // Restaure l'onglet actif (ou le 1er si son groupe a disparu, ex. après changement de monde).
    if (!groups.some((g) => g.title === activeGroupTitle)) activeGroupTitle = groups[0]!.title
    setActiveGroup(activeGroupTitle)
  }

  // Menu déroulant « Monde » : peuplé depuis WORLDS (libellés FR, aucune origine utilisateur),
  // valeur = monde courant. Un changement met à jour currentWorld (utilisé au Tester et à
  // l'Enregistrer ; compté comme modification par le garde anti-perte via savedWorld).
  function populateWorldSelect(): void {
    worldSelect.replaceChildren()
    for (const key of WORLD_KEYS) {
      const opt = document.createElement('option')
      opt.value = key
      opt.textContent = WORLDS[key].name
      worldSelect.appendChild(opt)
    }
    worldSelect.value = currentWorld
  }
  function onWorldChange(): void { currentWorld = toWorld(worldSelect.value); buildPalette(); redraw() } // signatures déco par biome + tuiles restylées

  // Sélecteur de TAILLE (Lot E) : peuplé depuis SIZE_OPTIONS ; changer la taille redimensionne la
  // grille (terrain + déco) en conservant le coin haut-gauche. La valeur reflète edCols×edRows.
  const sizeValue = (cols: number, rows: number): string => `${cols}x${rows}`
  function populateSizeSelect(): void {
    sizeSelect.replaceChildren()
    for (const s of SIZE_OPTIONS) {
      const opt = document.createElement('option')
      opt.value = sizeValue(s.cols, s.rows)
      opt.textContent = s.label
      sizeSelect.appendChild(opt)
    }
    // Taille chargée hors liste (ancien niveau atypique) : on l'ajoute pour ne pas la fausser à l'affichage.
    if (!SIZE_OPTIONS.some((s) => s.cols === edCols && s.rows === edRows)) {
      const opt = document.createElement('option')
      opt.value = sizeValue(edCols, edRows)
      opt.textContent = `${edCols}×${edRows}`
      sizeSelect.appendChild(opt)
    }
    sizeSelect.value = sizeValue(edCols, edRows)
  }
  function resizeGrid(nc: number, nr: number): void {
    if (nc === edCols && nr === edRows) return
    edCols = nc; edRows = nr
    rows = normalize(rows, nc, nr)             // conserve le coin haut-gauche (comble en '.', tronque)
    decoRows = normalize(decoRows, nc, nr)
    undoStack.length = 0; redoStack.length = 0 // dimensions changées : l'historique n'est plus applicable
    useEditorCanvas()                          // re-dimensionne le canvas éditeur
    buildPalette()                             // affiche/masque la palette déco selon la nouvelle taille
    hoverCell = -1
    redraw()
    refreshToolButtons()                       // historique vidé : grise Annuler/Refaire
    showStatus(`Taille de la carte : ${nc}×${nr}.`, 'info')
  }
  // Vrai si du contenu (terrain OU déco) se trouve HORS du cadre nc×nr (donc perdu si on réduit).
  function contentBeyond(nc: number, nr: number): boolean {
    for (let r = 0; r < edRows; r++) {
      const line = rows[r] ?? '', dline = decoRows[r] ?? ''
      for (let c = 0; c < edCols; c++) {
        if ((c >= nc || r >= nr) && ((line[c] ?? '.') !== '.' || (dline[c] ?? '.') !== '.')) return true
      }
    }
    return false
  }
  // Lit la valeur RÉELLE du <select> (« COLSxROWS »), pas seulement SIZE_OPTIONS : évite la désync
  // UI/état si une taille hors liste est sélectionnée (revue E). Confirme une réduction destructrice
  // (contenu hors cadre) avant de l'appliquer, et restaure l'affichage si l'utilisateur annule (revue B).
  function onSizeChange(): void {
    const m = /^(\d+)x(\d+)$/.exec(sizeSelect.value)
    if (!m) { sizeSelect.value = sizeValue(edCols, edRows); return }
    const nc = clampDim(+m[1]!, MAX_COLS), nr = clampDim(+m[2]!, MAX_ROWS)
    if (nc === edCols && nr === edRows) return
    if ((nc < edCols || nr < edRows) && contentBeyond(nc, nr) &&
        !confirm(`Réduire à ${nc}×${nr} supprimera le contenu hors du nouveau cadre. Continuer ?`)) {
      sizeSelect.value = sizeValue(edCols, edRows) // annulé : le menu réaffiche la taille courante
      return
    }
    resizeGrid(nc, nr)
  }

  // Menu déroulant « Vitesse des moines » (Task 3) : peuplé depuis MONK_SPEEDS (libellés FR
  // statiques), valeur = vitesse courante. Un changement met à jour currentMonkSpeed (utilisé
  // au Tester et à l'Enregistrer ; compté comme modification par le garde anti-perte).
  function populateMonkSpeedSelect(): void {
    monkSpeedSelect.replaceChildren()
    for (const speed of MONK_SPEEDS) {
      const opt = document.createElement('option')
      opt.value = speed
      opt.textContent = MONK_SPEED_LABELS[speed]
      monkSpeedSelect.appendChild(opt)
    }
    monkSpeedSelect.value = currentMonkSpeed
  }
  function onMonkSpeedChange(): void { currentMonkSpeed = toMonkSpeed(monkSpeedSelect.value) }

  // Case à cocher « Moines malins » (IA maligne, smartMonks) : reflète currentSmartMonks à
  // l'ouverture ; un changement met à jour currentSmartMonks (utilisé au Tester et à l'Enregistrer ;
  // compté comme modification par le garde anti-perte via savedSmartMonks).
  function syncSmartMonksCheckbox(): void { smartMonksCheckbox.checked = currentSmartMonks; monkPowersCheckbox.checked = currentMonkPowers }
  function onSmartMonksChange(): void { currentSmartMonks = smartMonksCheckbox.checked }
  function onMonkPowersChange(): void { currentMonkPowers = monkPowersCheckbox.checked }

  // Tiroir Réglages (Phase 3 L3) : ⚙ ouvre/ferme le panneau (monde/taille/moines) au mobile.
  // Sans effet visuel au desktop (le CSS `pointer: fine` garde les réglages inline).
  function onSettingsToggle(): void {
    const open = settingsPanel.classList.toggle('is-open')
    settingsToggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false')
  }

  // --- montage / démontage -------------------------------------------------
  function attachEditorListeners(): void {
    worldSelect.addEventListener('change', onWorldChange)
    sizeSelect.addEventListener('change', onSizeChange)
    monkSpeedSelect.addEventListener('change', onMonkSpeedChange)
    smartMonksCheckbox.addEventListener('change', onSmartMonksChange)
    monkPowersCheckbox.addEventListener('change', onMonkPowersChange)
    settingsToggleBtn.addEventListener('click', onSettingsToggle)
    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('pointerup', onPointerUp)
    canvas.addEventListener('pointercancel', onPointerUp)
    canvas.addEventListener('pointerleave', onPointerLeave)
    canvas.addEventListener('contextmenu', onContextMenu)
    canvas.addEventListener('wheel', onWheel, { passive: false }) // zoom molette desktop (L2)
    // Filet : si setPointerCapture a échoué (canvas partagé/déplacé) et que le doigt relâche
    // hors du canvas, le pointerup remonte quand même jusqu'à window -> le trait/geste se termine.
    addEventListener('pointerup', onPointerUp)
    addEventListener('pointercancel', onPointerUp)
    addEventListener('keydown', onKey)
    testBtn.addEventListener('click', handleTest)
    saveBtn.addEventListener('click', onSaveClick)
    quitBtn.addEventListener('click', handleQuit)
    undoBtn.addEventListener('click', onUndoClick)
    redoBtn.addEventListener('click', onRedoClick)
    eraseBtn.addEventListener('click', onEraseClick)
    recenterBtn.addEventListener('click', recenter)
    zenBtn.addEventListener('click', onZenOn)
    zenExitBtn.addEventListener('click', onZenOff)
  }
  function detachEditorListeners(): void {
    // Réinitialise un trait en cours AVANT de retirer les écouteurs : si l'éditeur se détache
    // pendant qu'un doigt peint (ex. 2e doigt sur « Tester »), le pointerup ne sera plus reçu ;
    // sans ce reset, `painting` resterait true et onPointerDown ('if painting return') bloquerait
    // le dessin définitivement au retour dans l'éditeur.
    if (painting && activePointerId !== -1) {
      try { canvas.releasePointerCapture(activePointerId) } catch { /* déjà relâché */ }
    }
    painting = false; activePointerId = -1; strokeBefore = null; lastCell = -1
    pointers.clear(); gesture = null // zoom/pan (L2) : aucun doigt/geste ne survit au détachement
    settingsPanel.classList.remove('is-open'); settingsToggleBtn.setAttribute('aria-expanded', 'false') // tiroir refermé (L3)
    root.classList.remove('ed-zen'); zenBtn.setAttribute('aria-pressed', 'false') // plein écran refermé (C1) : jamais collant
    worldSelect.removeEventListener('change', onWorldChange)
    sizeSelect.removeEventListener('change', onSizeChange)
    monkSpeedSelect.removeEventListener('change', onMonkSpeedChange)
    smartMonksCheckbox.removeEventListener('change', onSmartMonksChange)
    monkPowersCheckbox.removeEventListener('change', onMonkPowersChange)
    settingsToggleBtn.removeEventListener('click', onSettingsToggle)
    canvas.removeEventListener('pointerdown', onPointerDown)
    canvas.removeEventListener('pointermove', onPointerMove)
    canvas.removeEventListener('pointerup', onPointerUp)
    canvas.removeEventListener('pointercancel', onPointerUp)
    canvas.removeEventListener('pointerleave', onPointerLeave)
    canvas.removeEventListener('contextmenu', onContextMenu)
    canvas.removeEventListener('wheel', onWheel)
    removeEventListener('pointerup', onPointerUp)
    removeEventListener('pointercancel', onPointerUp)
    removeEventListener('keydown', onKey)
    testBtn.removeEventListener('click', handleTest)
    saveBtn.removeEventListener('click', onSaveClick)
    quitBtn.removeEventListener('click', handleQuit)
    undoBtn.removeEventListener('click', onUndoClick)
    redoBtn.removeEventListener('click', onRedoClick)
    eraseBtn.removeEventListener('click', onEraseClick)
    recenterBtn.removeEventListener('click', recenter)
    zenBtn.removeEventListener('click', onZenOn)
    zenExitBtn.removeEventListener('click', onZenOff)
  }
  function teardown(): void {
    if (stopped) return
    stopped = true
    testHandle?.stop() // défensif : si l'éditeur est fermé pendant un test, arrêter la partie
    testHandle = null
    detachEditorListeners()
    useGameCanvas() // rend le canvas au jeu (dimensions + emplacement d'origine)
    root.hidden = true
  }

  nameInput.value = opts.initial?.name ?? ''
  populateWorldSelect()
  populateSizeSelect()
  populateMonkSpeedSelect()
  syncSmartMonksCheckbox()
  buildPalette()
  refreshToolButtons() // grise Annuler/Refaire au démarrage (historique vide)
  attachEditorListeners()
  root.hidden = false
  showStatus(
    currentId === null
      ? 'Nouvelle carte : dessine (doigt/souris), Gomme pour effacer, pince à 2 doigts pour zoomer.'
      : 'Carte chargée. Modifie puis enregistre.',
    'info',
  )
  redraw()
  return { stop: teardown }
}
